/**
 * Privacy Policy Route
 *
 * Full GDPR-compliant privacy policy (Story 0.7) with:
 * - Layered summary (Art. 12 transparency)
 * - Platform-specific data access tables
 * - Third-party processor disclosures
 * - Data retention periods
 * - User rights and exercise methods
 * - AI/automated decision-making disclosure (Art. 22)
 * - User data vs. end-client data distinction
 */

import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { Card, CardContent } from "@provenleap/ui";
import { useSEO } from "../hooks/useSEO";
import { SEO_CONFIG } from "../seo/seoConfig";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  const router = useRouter();

  // Title/description/canonical/OG with cleanup (Story 30.2 useSEO).
  useSEO(SEO_CONFIG["/privacy"]!);

  return (
    <div id="top" className="min-h-screen bg-white p-4 sm:p-8">
      <div className="max-w-[800px] mx-auto">
        <div className="flex items-center gap-4 mb-4">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) {
                router.history.back();
              } else {
                router.navigate({ to: "/login" });
              }
            }}
            className="text-sm text-muted-foreground hover:underline inline-block cursor-pointer"
          >
            &larr; Back
          </button>
          <span className="text-sm text-muted-foreground">|</span>
          <Link
            to="/terms"
            className="text-sm text-muted-foreground hover:underline inline-block"
          >
            See also our Terms of Service
          </Link>
        </div>

        <article>
          <Card>
            <CardContent className="pt-6">
              {/* Header */}
              <header className="mb-8">
                <h1 className="text-2xl font-semibold mb-2">Privacy Policy</h1>
                <p className="text-sm text-muted-foreground">
                  Version 1.0 | Effective Date: February 6, 2026 | Last Updated:
                  February 6, 2026
                </p>
              </header>

              {/* Layered Summary */}
              <section className="mb-8 rounded-lg border border-border bg-neutral-50 p-4">
                <h2 className="text-lg font-medium mb-3">
                  Key Points at a Glance
                </h2>
                <ul className="list-disc pl-5 space-y-1.5 text-base leading-relaxed max-w-prose">
                  <li>
                    We collect your email, password, and profile info to provide
                    your account.
                  </li>
                  <li>
                    We access analytics data from platforms you connect (Google,
                    Meta, LinkedIn, TikTok, etc.) on behalf of your agency
                    clients.
                  </li>
                  <li>
                    Your data is stored on Cloudflare infrastructure, primarily
                    in the EU.
                  </li>
                  <li>
                    We share data with specific service providers (listed below)
                    solely to operate the service.
                  </li>
                  <li>
                    You can request access, correction, or deletion of your data
                    at any time.
                  </li>
                  <li>
                    We use AI (Anthropic Claude) to generate report insights
                    &mdash; these are advisory only, not automated
                    decision-making with legal effects.
                  </li>
                  <li>
                    We use only essential cookies for session management &mdash;
                    no tracking or analytics cookies.
                  </li>
                  <li>
                    Questions? Contact us at{" "}
                    <a
                      href="mailto:privacy@provenleap.com"
                      className="underline hover:text-foreground"
                    >
                      privacy@provenleap.com
                    </a>
                  </li>
                </ul>
              </section>

              {/* Table of Contents */}
              <nav aria-label="Table of contents" className="mb-8">
                <h2 className="text-lg font-medium mb-3">Table of Contents</h2>
                <ol className="list-decimal pl-5 space-y-1 text-sm">
                  <li>
                    <a href="#identity" className="underline hover:text-foreground">
                      Identity &amp; Contact Details
                    </a>
                  </li>
                  <li>
                    <a href="#data-we-collect" className="underline hover:text-foreground">
                      Data We Collect
                    </a>
                  </li>
                  <li>
                    <a href="#how-we-use" className="underline hover:text-foreground">
                      How We Use Your Data
                    </a>
                  </li>
                  <li>
                    <a href="#third-party-processors" className="underline hover:text-foreground">
                      Third-Party Processors
                    </a>
                  </li>
                  <li>
                    <a href="#international-transfers" className="underline hover:text-foreground">
                      International Data Transfers
                    </a>
                  </li>
                  <li>
                    <a href="#data-retention" className="underline hover:text-foreground">
                      Data Retention
                    </a>
                  </li>
                  <li>
                    <a href="#your-rights" className="underline hover:text-foreground">
                      Your Rights and How to Exercise Them
                    </a>
                  </li>
                  <li>
                    <a href="#data-security" className="underline hover:text-foreground">
                      Data Security
                    </a>
                  </li>
                  <li>
                    <a href="#cookies" className="underline hover:text-foreground">
                      Cookies
                    </a>
                  </li>
                  <li>
                    <a href="#automated-decisions" className="underline hover:text-foreground">
                      Automated Decision-Making &amp; AI
                    </a>
                  </li>
                  <li>
                    <a href="#children" className="underline hover:text-foreground">
                      Children&apos;s Data
                    </a>
                  </li>
                  <li>
                    <a href="#user-vs-client-data" className="underline hover:text-foreground">
                      User Data vs. End-Client Data
                    </a>
                  </li>
                  <li>
                    <a href="#changes" className="underline hover:text-foreground">
                      Changes to This Policy
                    </a>
                  </li>
                </ol>
              </nav>

              {/* Section 1: Identity & Contact */}
              <section id="identity" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  1. Identity &amp; Contact Details
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    <strong>Data Controller:</strong> ProvenLeap SRL, Bucharest,
                    Romania
                  </p>
                  <p>
                    <strong>Data Protection Contact:</strong>{" "}
                    <a
                      href="mailto:privacy@provenleap.com"
                      className="underline hover:text-foreground"
                    >
                      privacy@provenleap.com
                    </a>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Note: ProvenLeap does not currently appoint a Data Protection
                    Officer (DPO) under GDPR Article 37(1), as our core
                    activities do not involve large-scale systematic monitoring
                    of individuals or large-scale processing of special category
                    data. A dedicated data protection contact email is provided
                    for all privacy-related inquiries. We will reassess DPO
                    appointment as the service scales.
                  </p>
                  <p>
                    <strong>Supervisory Authority:</strong> ANSPDCP (Autoritatea
                    Nationala de Supraveghere a Prelucrarii Datelor cu Caracter
                    Personal)
                  </p>
                  <p>
                    Address: B-dul G-ral. Gheorghe Magheru 28-30, Sector 1,
                    010336 Bucharest, Romania
                    <br />
                    Website:{" "}
                    <a
                      href="https://www.dataprotection.ro"
                      className="underline hover:text-foreground"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://www.dataprotection.ro
                    </a>
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 2: Data We Collect */}
              <section id="data-we-collect" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  2. Data We Collect
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-4">
                  <p>We collect the following categories of data:</p>

                  <h3 className="text-lg font-medium mt-4">Account Data</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      Email address and password hash (for email/password
                      registration)
                    </li>
                    <li>
                      Display name and profile information you choose to provide
                    </li>
                    <li>
                      OAuth profile data (name, email, avatar) when using Google
                      sign-in
                    </li>
                  </ul>

                  <h3 className="text-lg font-medium mt-4">OAuth Tokens</h3>
                  <p>
                    When you connect third-party platforms, we store encrypted
                    OAuth access and refresh tokens to maintain your
                    integrations. These tokens allow us to access data from the
                    connected platforms on your behalf.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Analytics Data from Integrations
                  </h3>
                  <p>
                    We access analytics and performance data from the platforms
                    you connect. This data is accessed on your behalf to generate
                    reports for your agency clients. See the{" "}
                    <a href="#user-vs-client-data" className="underline hover:text-foreground">
                      User Data vs. End-Client Data
                    </a>{" "}
                    section for how we distinguish between your data and your
                    clients&apos; data.
                  </p>

                  <h3 className="text-lg font-medium mt-4">Usage Data</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      Feature usage patterns (which features you use, how
                      frequently)
                    </li>
                    <li>Page views and navigation patterns within ProvenLeap</li>
                    <li>Error logs for debugging and service improvement</li>
                  </ul>

                  <h3 className="text-lg font-medium mt-4">
                    Essential Cookies
                  </h3>
                  <p>
                    A single session cookie for authentication. See the{" "}
                    <a href="#cookies" className="underline hover:text-foreground">
                      Cookies
                    </a>{" "}
                    section for details.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Platform-Specific Data Access
                  </h3>
                  <p>
                    The following table details the specific permission scopes we
                    request from each platform and the data types accessed
                    through each scope:
                  </p>
                  <div className="overflow-x-auto mt-3">
                    <table className="min-w-full text-sm border border-border">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Platform
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Permission Scope
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Data Types Accessed
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Google Analytics 4
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">analytics.readonly</code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Sessions, users, pageviews, bounce rate, events,
                            conversions
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Google Search Console
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">
                              webmasters.readonly
                            </code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Search queries, impressions, clicks, CTR, position
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Google Ads
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">adwords</code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Campaigns, ad spend, impressions, clicks,
                            conversions, ROAS
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            YouTube Analytics
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">
                              yt-analytics.readonly
                            </code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Views, watch time, subscribers, engagement,
                            demographics
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Google Business Profile
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">business.manage</code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Reviews, ratings, posts, photos, insights
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Google PageSpeed
                          </td>
                          <td className="border border-border px-3 py-2">
                            Public API (no OAuth)
                          </td>
                          <td className="border border-border px-3 py-2">
                            Performance scores, Core Web Vitals, recommendations
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Facebook Pages
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">pages_show_list</code>,{" "}
                            <code className="text-xs">
                              pages_read_engagement
                            </code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Page names, IDs, impressions, reach, engaged users,
                            post performance
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Facebook Ads
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">ads_read</code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Campaign names, spend, impressions, clicks,
                            conversions
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Instagram
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">instagram_basic</code>,{" "}
                            <code className="text-xs">
                              instagram_manage_insights
                            </code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Profile info, media, impressions, reach, engagement
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            LinkedIn Pages
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">
                              r_organization_social
                            </code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Company page analytics, follower counts, engagement
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            LinkedIn Ads
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">r_ads_reporting</code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Campaign performance, spend, impressions, clicks
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            TikTok Business
                          </td>
                          <td className="border border-border px-3 py-2">
                            <code className="text-xs">analytics.read</code>
                          </td>
                          <td className="border border-border px-3 py-2">
                            Video views, engagement, follower growth,
                            demographics
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 3: How We Use Your Data */}
              <section id="how-we-use" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  3. How We Use Your Data
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-4">
                  <p>
                    We process your data only for specific, legitimate purposes.
                    Below is a breakdown of each processing activity and its
                    legal basis under GDPR Article 6(1):
                  </p>
                  <div className="overflow-x-auto mt-3">
                    <table className="min-w-full text-sm border border-border">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Data
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Purpose
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Legal Basis
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Email, password
                          </td>
                          <td className="border border-border px-3 py-2">
                            Account creation and authentication
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contract (Art. 6(1)(b))
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            OAuth tokens
                          </td>
                          <td className="border border-border px-3 py-2">
                            Platform integration access
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contract (Art. 6(1)(b))
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Analytics data (your platforms)
                          </td>
                          <td className="border border-border px-3 py-2">
                            Report generation
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contract (Art. 6(1)(b))
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            End-client analytics data
                          </td>
                          <td className="border border-border px-3 py-2">
                            Report generation for agency clients
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contract (Art. 6(1)(b)) &mdash; ProvenLeap as
                            processor
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Usage data
                          </td>
                          <td className="border border-border px-3 py-2">
                            Service improvement and debugging
                          </td>
                          <td className="border border-border px-3 py-2">
                            Legitimate interest (Art. 6(1)(f))
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Email address
                          </td>
                          <td className="border border-border px-3 py-2">
                            Transactional emails (verification, password reset,
                            notifications)
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contract (Art. 6(1)(b))
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Analytics data
                          </td>
                          <td className="border border-border px-3 py-2">
                            AI-powered insight generation
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contract (Art. 6(1)(b))
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Essential cookies
                          </td>
                          <td className="border border-border px-3 py-2">
                            Session management
                          </td>
                          <td className="border border-border px-3 py-2">
                            Legitimate interest (Art. 6(1)(f))
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>
                    <strong>Legitimate Interests Assessment:</strong> Where we
                    rely on legitimate interest, we have assessed that our
                    processing is necessary for our legitimate purposes and does
                    not override your fundamental rights and freedoms. You may
                    object to processing based on legitimate interest at any
                    time. See{" "}
                    <a href="#your-rights" className="underline hover:text-foreground">
                      Your Rights
                    </a>
                    .
                  </p>
                  <p>
                    <strong>Provision of data:</strong> Providing your email and
                    password (or OAuth credentials) is necessary to create an
                    account and use the service. Without this data, we cannot
                    provide the service to you.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 4: Third-Party Processors */}
              <section id="third-party-processors" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  4. Third-Party Processors
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-4">
                  <p>
                    We share your data with the following third-party service
                    providers solely to operate and deliver the ProvenLeap
                    service. Each processor operates under a Data Processing
                    Agreement (DPA) that ensures GDPR compliance.
                  </p>
                  <div className="overflow-x-auto mt-3">
                    <table className="min-w-full text-sm border border-border">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Processor
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Purpose
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Data Transferred
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Location
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Transfer Mechanism
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Cloudflare
                          </td>
                          <td className="border border-border px-3 py-2">
                            Infrastructure (Workers, D1, KV, R2, Pages)
                          </td>
                          <td className="border border-border px-3 py-2">
                            All application data
                          </td>
                          <td className="border border-border px-3 py-2">
                            Global (EU preferred)
                          </td>
                          <td className="border border-border px-3 py-2">
                            DPA + SCCs
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Anthropic
                          </td>
                          <td className="border border-border px-3 py-2">
                            AI insights generation
                          </td>
                          <td className="border border-border px-3 py-2">
                            Analytics data (anonymized)
                          </td>
                          <td className="border border-border px-3 py-2">
                            USA
                          </td>
                          <td className="border border-border px-3 py-2">
                            DPA + SCCs
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Amazon SES
                          </td>
                          <td className="border border-border px-3 py-2">
                            Transactional email delivery
                          </td>
                          <td className="border border-border px-3 py-2">
                            Email addresses, email content
                          </td>
                          <td className="border border-border px-3 py-2">
                            EU (eu-west-1)
                          </td>
                          <td className="border border-border px-3 py-2">
                            DPA + SCCs
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Stripe *
                          </td>
                          <td className="border border-border px-3 py-2">
                            Payment processing
                          </td>
                          <td className="border border-border px-3 py-2">
                            Name, email, payment method
                          </td>
                          <td className="border border-border px-3 py-2">
                            USA/EU
                          </td>
                          <td className="border border-border px-3 py-2">
                            DPA + EU-US DPF
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Google APIs *
                          </td>
                          <td className="border border-border px-3 py-2">
                            Analytics, Search Console, Ads, YouTube, GMB,
                            PageSpeed
                          </td>
                          <td className="border border-border px-3 py-2">
                            OAuth tokens, analytics metrics
                          </td>
                          <td className="border border-border px-3 py-2">
                            USA
                          </td>
                          <td className="border border-border px-3 py-2">
                            DPA + EU-US DPF
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Meta APIs
                          </td>
                          <td className="border border-border px-3 py-2">
                            Facebook Pages, Facebook Ads, Instagram
                          </td>
                          <td className="border border-border px-3 py-2">
                            OAuth tokens, social metrics
                          </td>
                          <td className="border border-border px-3 py-2">
                            USA
                          </td>
                          <td className="border border-border px-3 py-2">
                            DPA + SCCs
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            LinkedIn API
                          </td>
                          <td className="border border-border px-3 py-2">
                            Company Pages, Ads
                          </td>
                          <td className="border border-border px-3 py-2">
                            OAuth tokens, marketing metrics
                          </td>
                          <td className="border border-border px-3 py-2">
                            USA
                          </td>
                          <td className="border border-border px-3 py-2">
                            LinkedIn Business DPA
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            TikTok API
                          </td>
                          <td className="border border-border px-3 py-2">
                            Business analytics
                          </td>
                          <td className="border border-border px-3 py-2">
                            OAuth tokens, content metrics
                          </td>
                          <td className="border border-border px-3 py-2">
                            Singapore/USA
                          </td>
                          <td className="border border-border px-3 py-2">
                            DPA + SCCs
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    * Denotes processors certified under the EU-US Data Privacy
                    Framework (DPF). All other processors rely on Standard
                    Contractual Clauses (SCCs) as the transfer mechanism.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 5: International Data Transfers */}
              <section id="international-transfers" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  5. International Data Transfers
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    Your data is primarily stored and processed within the
                    European Union using Cloudflare&apos;s infrastructure.
                    Cloudflare may process data in other regions for network
                    performance purposes, subject to Standard Contractual
                    Clauses.
                  </p>
                  <p>
                    When we transfer personal data outside the European Economic
                    Area (EEA), we rely on the following safeguards:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong>EU-US Data Privacy Framework (DPF):</strong>{" "}
                      Certain processors (Stripe, Google) are certified under
                      the DPF, which the European Commission has recognized as
                      providing adequate protection for personal data transfers
                      to certified US organizations.
                    </li>
                    <li>
                      <strong>
                        Standard Contractual Clauses (SCCs):
                      </strong>{" "}
                      For all other international transfers, we use the European
                      Commission&apos;s Standard Contractual Clauses, which
                      provide appropriate safeguards for the protection of
                      personal data. This applies to transfers to Anthropic,
                      Meta, TikTok, and Cloudflare.
                    </li>
                  </ul>
                  <p>
                    You may request a copy of the relevant safeguards by
                    contacting us at{" "}
                    <a
                      href="mailto:privacy@provenleap.com"
                      className="underline hover:text-foreground"
                    >
                      privacy@provenleap.com
                    </a>
                    .
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 6: Data Retention */}
              <section id="data-retention" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  6. Data Retention
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-4">
                  <p>
                    We retain your data only for as long as necessary to provide
                    our services and fulfill the purposes described in this
                    policy. Specific retention periods are:
                  </p>
                  <div className="overflow-x-auto mt-3">
                    <table className="min-w-full text-sm border border-border">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Data Category
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Retention Period
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Basis
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Account data
                          </td>
                          <td className="border border-border px-3 py-2">
                            Duration of account + 30 days after deletion
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contractual
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            OAuth tokens
                          </td>
                          <td className="border border-border px-3 py-2">
                            Until you disconnect the integration or delete your
                            account
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contractual
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Analytics/report data (general)
                          </td>
                          <td className="border border-border px-3 py-2">
                            Duration of account
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contractual
                          </td>
                        </tr>
                        <tr className="bg-amber-50">
                          <td className="border border-border px-3 py-2 font-medium">
                            LinkedIn social activity data
                          </td>
                          <td className="border border-border px-3 py-2 font-medium">
                            Maximum 48 hours
                          </td>
                          <td className="border border-border px-3 py-2">
                            LinkedIn API Terms
                          </td>
                        </tr>
                        <tr className="bg-amber-50">
                          <td className="border border-border px-3 py-2 font-medium">
                            LinkedIn profile data
                          </td>
                          <td className="border border-border px-3 py-2 font-medium">
                            Deleted within 24 hours
                          </td>
                          <td className="border border-border px-3 py-2">
                            LinkedIn API Terms
                          </td>
                        </tr>
                        <tr className="bg-amber-50">
                          <td className="border border-border px-3 py-2 font-medium">
                            Meta integration data
                          </td>
                          <td className="border border-border px-3 py-2 font-medium">
                            While integration is connected; deleted on
                            disconnect
                          </td>
                          <td className="border border-border px-3 py-2">
                            Meta Platform Terms
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Cached metrics (KV)
                          </td>
                          <td className="border border-border px-3 py-2">
                            24 hours
                          </td>
                          <td className="border border-border px-3 py-2">
                            Technical necessity
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Generated PDFs (R2)
                          </td>
                          <td className="border border-border px-3 py-2">
                            Duration of account
                          </td>
                          <td className="border border-border px-3 py-2">
                            Contractual
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Transactional email logs
                          </td>
                          <td className="border border-border px-3 py-2">
                            90 days
                          </td>
                          <td className="border border-border px-3 py-2">
                            Legitimate interest
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Payment records
                          </td>
                          <td className="border border-border px-3 py-2">
                            7 years
                          </td>
                          <td className="border border-border px-3 py-2">
                            Legal obligation (Romanian accounting law, Art.
                            6(1)(c))
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Audit logs
                          </td>
                          <td className="border border-border px-3 py-2">
                            2 years
                          </td>
                          <td className="border border-border px-3 py-2">
                            Legitimate interest
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>
                    When your account is deleted, we will delete or anonymize
                    your personal data within 30 days, except where longer
                    retention is required by law (e.g., payment records for tax
                    compliance).
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 7: Your Rights */}
              <section id="your-rights" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  7. Your Rights and How to Exercise Them
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-4">
                  <p>
                    Under the GDPR, you have the following rights regarding your
                    personal data:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong>Right of Access (Art. 15):</strong> Request a copy
                      of the personal data we hold about you.
                    </li>
                    <li>
                      <strong>Right to Rectification (Art. 16):</strong> Request
                      correction of inaccurate or incomplete personal data.
                    </li>
                    <li>
                      <strong>Right to Erasure (Art. 17):</strong> Request
                      deletion of your personal data (&ldquo;right to be
                      forgotten&rdquo;).
                    </li>
                    <li>
                      <strong>Right to Restriction (Art. 18):</strong> Request
                      that we limit how we process your data in certain
                      circumstances.
                    </li>
                    <li>
                      <strong>Right to Data Portability (Art. 20):</strong>{" "}
                      Receive your personal data in a structured,
                      commonly-used, machine-readable format.
                    </li>
                    <li>
                      <strong>Right to Object (Art. 21):</strong> Object to
                      processing based on legitimate interest.
                    </li>
                    <li>
                      <strong>Right to Withdraw Consent:</strong> Where
                      processing is based on consent, withdraw that consent at
                      any time without affecting the lawfulness of prior
                      processing.
                    </li>
                  </ul>

                  <h3 className="text-lg font-medium mt-4">
                    How to Exercise Your Rights
                  </h3>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>
                      <strong>Self-service (in-app):</strong> Use Settings &gt;
                      Delete Account to delete your account and all associated
                      data.
                    </li>
                    <li>
                      <strong>Email:</strong> Send your request to{" "}
                      <a
                        href="mailto:privacy@provenleap.com"
                        className="underline hover:text-foreground"
                      >
                        privacy@provenleap.com
                      </a>
                      .
                    </li>
                    <li>
                      <strong>Platform-specific deletion:</strong>{" "}
                      Platform-specific programmatic deletion callbacks will be
                      available when integrations are fully deployed (Meta Data
                      Deletion Callback, etc.).
                    </li>
                  </ol>

                  <p>
                    <strong>Response time:</strong> We will respond to your
                    request within <strong>30 days</strong> as required by the
                    GDPR. We target a response within 72 hours.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Lodge a Complaint
                  </h3>
                  <p>
                    If you believe your data protection rights have been
                    violated, you have the right to lodge a complaint with your
                    local supervisory authority. Our lead supervisory authority
                    is:
                  </p>
                  <p>
                    <strong>ANSPDCP</strong> (Autoritatea Nationala de
                    Supraveghere a Prelucrarii Datelor cu Caracter Personal)
                    <br />
                    B-dul G-ral. Gheorghe Magheru 28-30, Sector 1, 010336
                    Bucharest, Romania
                    <br />
                    Website:{" "}
                    <a
                      href="https://www.dataprotection.ro"
                      className="underline hover:text-foreground"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://www.dataprotection.ro
                    </a>
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 8: Data Security */}
              <section id="data-security" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  8. Data Security
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    We implement appropriate technical and organizational
                    measures to protect your personal data against unauthorized
                    access, alteration, disclosure, or destruction:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong>Encryption at rest:</strong> All data stored in
                      Cloudflare D1 is encrypted at rest using
                      Cloudflare&apos;s default encryption.
                    </li>
                    <li>
                      <strong>Encryption in transit:</strong> All connections use
                      TLS 1.3, enforced automatically by Cloudflare.
                    </li>
                    <li>
                      <strong>Per-tenant data isolation:</strong> Each user
                      account&apos;s data is isolated using tenant-scoped
                      database queries, preventing cross-account data access.
                    </li>
                    <li>
                      <strong>Session security:</strong> Authentication sessions
                      use HttpOnly, Secure, SameSite=Lax cookies, preventing
                      client-side script access and cross-site request forgery.
                    </li>
                    <li>
                      <strong>Password security:</strong> Passwords are hashed
                      using industry-standard algorithms and are never stored in
                      plain text.
                    </li>
                    <li>
                      <strong>OAuth token encryption:</strong> Third-party
                      platform tokens are stored encrypted and are only decrypted
                      when needed to access platform APIs.
                    </li>
                  </ul>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 9: Cookies */}
              <section id="cookies" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">9. Cookies</h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    ProvenLeap uses <strong>only essential cookies</strong>{" "}
                    required for the service to function. We do not use any
                    analytics, advertising, or tracking cookies.
                  </p>
                  <div className="overflow-x-auto mt-3">
                    <table className="min-w-full text-sm border border-border">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Cookie
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Purpose
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Type
                          </th>
                          <th className="border border-border px-3 py-2 text-left font-medium">
                            Flags
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-border px-3 py-2">
                            Session cookie
                          </td>
                          <td className="border border-border px-3 py-2">
                            Maintains your authenticated session
                          </td>
                          <td className="border border-border px-3 py-2">
                            Essential
                          </td>
                          <td className="border border-border px-3 py-2">
                            HttpOnly, Secure, SameSite=Lax
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>
                    Because we only use strictly necessary cookies, no cookie
                    consent banner is required under the ePrivacy Directive
                    (Article 5(3) exemption for essential cookies). If
                    non-essential cookies are introduced in the future, a consent
                    mechanism will be implemented before they are activated. For
                    the complete engineering inventory of cookies and client-side
                    storage used by ProvenLeap, see{" "}
                    <code>docs/compliance/cookies.md</code> in our source
                    repository.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 10: Automated Decision-Making & AI */}
              <section id="automated-decisions" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  10. Automated Decision-Making &amp; AI
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    ProvenLeap uses artificial intelligence (provided by
                    Anthropic Claude) to generate report insights and
                    recommendations based on your analytics data. This
                    disclosure is made pursuant to GDPR Articles 13(2)(f) and
                    22.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Logic Involved
                  </h3>
                  <p>
                    Our AI analyzes aggregated analytics data from your connected
                    platforms (traffic patterns, engagement metrics, campaign
                    performance) to generate executive summaries, identify
                    trends, detect anomalies, and provide actionable
                    recommendations for your marketing reports.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Significance and Consequences
                  </h3>
                  <p>
                    AI-generated insights are <strong>advisory only</strong>.
                    They do not constitute automated decision-making with legal
                    or similarly significant effects as defined by GDPR Article
                    22. All insights are presented as suggestions within
                    reports; no automated actions are taken based on AI output.
                    You retain full control over whether to include, edit, or
                    discard AI-generated content in your reports.
                  </p>

                  <h3 className="text-lg font-medium mt-4">Your Rights</h3>
                  <p>
                    You have the right to request human review of any
                    AI-generated insight, express your point of view, and contest
                    the output. Contact us at{" "}
                    <a
                      href="mailto:privacy@provenleap.com"
                      className="underline hover:text-foreground"
                    >
                      privacy@provenleap.com
                    </a>{" "}
                    for any concerns about AI processing.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Data Sent to AI Provider
                  </h3>
                  <p>
                    Only aggregated, anonymized analytics data is sent to our AI
                    provider (Anthropic). Personal identifiable information is
                    stripped before processing. Anthropic does not use your data
                    to train their models.
                  </p>

                  {/* DRAFT — MCP connector data-access disclosure (Story 29-16, required by
                      the Claude Connectors + ChatGPT App directories). Pending Calin/legal
                      ratification of wording before directory submission. */}
                  <h3 className="text-lg font-medium mt-4">
                    AI Assistant Access (MCP Connector)
                  </h3>
                  <p>
                    ProvenLeap offers an <strong>optional</strong> connector (built on the
                    Model Context Protocol) that lets you link your account to third-party
                    AI assistants such as Anthropic Claude, OpenAI ChatGPT, and other
                    MCP-compatible clients, so you can ask questions about your analytics
                    conversationally. The connector is <strong>off by default</strong> and is
                    enabled only when you explicitly authorize it.
                  </p>
                  <p>
                    <strong>What it can access:</strong> access to <em>your own</em>{" "}
                    marketing analytics within your account — your clients, reports, and
                    metric summaries. It is strictly tenant-scoped and cannot read any
                    other customer&apos;s data. If you grant the optional draft-creation
                    permission on the consent screen, the assistant can additionally
                    create <strong>draft</strong> reports in your account; drafts are
                    never published, shared, or sent to your clients without your
                    explicit action in ProvenLeap, and the assistant can never modify or
                    delete anything.
                  </p>
                  <p>
                    <strong>Authorization &amp; control:</strong> you grant access through a
                    standard OAuth consent screen and can revoke it at any time from{" "}
                    <em>Settings &rarr; AI Access</em>. When you ask a question through a
                    connected assistant, the analytics needed to answer it are sent to that
                    assistant&apos;s provider (e.g. Anthropic or OpenAI), whose own privacy
                    terms govern their handling. Responses are passed through an automated
                    sanitization layer that redacts secrets and personal data before they
                    leave our servers, and we never sell this data. Connector activity is
                    logged for security (the tool invoked and a timestamp — never the raw
                    text of your questions).
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 11: Children's Data */}
              <section id="children" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  11. Children&apos;s Data
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    ProvenLeap is a business-to-business service designed for
                    marketing agencies and professionals. Our service is not
                    intended for users under 16 years of age, in accordance with
                    GDPR Article 8.
                  </p>
                  <p>
                    We do not knowingly collect personal data from children under
                    16. If we become aware that we have collected data from a
                    child under 16, we will take steps to delete that data
                    promptly. If you believe a child under 16 has provided us
                    with personal data, please contact us at{" "}
                    <a
                      href="mailto:privacy@provenleap.com"
                      className="underline hover:text-foreground"
                    >
                      privacy@provenleap.com
                    </a>
                    .
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 12: User Data vs. End-Client Data */}
              <section id="user-vs-client-data" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  12. User Data vs. End-Client Data
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    ProvenLeap serves marketing agencies who connect their
                    clients&apos; platforms. It is important to understand the
                    distinction between different types of data we handle:
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Your Account Data (ProvenLeap as Data Controller)
                  </h3>
                  <p>
                    This includes your email, password, profile information,
                    billing data, and usage patterns. This is your personal data
                    as a ProvenLeap subscriber. We determine the purposes and
                    means of processing this data.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    End-Client Analytics Data (ProvenLeap as Data Processor)
                  </h3>
                  <p>
                    This includes Google Analytics metrics, social media
                    engagement data, ad performance data, and other analytics
                    accessed through OAuth integrations on behalf of your agency
                    clients. This data belongs to your agency&apos;s clients.
                  </p>
                  <p>
                    ProvenLeap processes end-client data{" "}
                    <strong>solely to provide the reporting service</strong> as
                    instructed by you (the agency user). We do not use end-client
                    data for any other purpose.
                  </p>
                  <p>
                    As the agency, you are the data controller for your
                    clients&apos; data and are responsible for having appropriate
                    data processing agreements with your own clients, as well as
                    ensuring you have the necessary authorization to connect
                    their platforms to ProvenLeap.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 13: Changes to This Policy */}
              <section id="changes" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  13. Changes to This Policy
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    We may update this Privacy Policy from time to time to
                    reflect changes in our practices, technology, or legal
                    requirements.
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong>Notification:</strong> We will notify you of
                      material changes via email to the address associated with
                      your account at least <strong>30 days</strong> before the
                      changes take effect.
                    </li>
                    <li>
                      <strong>Versioning:</strong> Each version of this policy
                      is identified by a version number and effective date at the
                      top of this page. Previous versions are retained in our
                      version control system and can be provided upon request.
                    </li>
                    <li>
                      <strong>Non-material changes:</strong> Minor clarifications
                      or formatting changes that do not affect your rights will
                      be updated without prior notification, with the
                      &ldquo;Last Updated&rdquo; date reflecting the change.
                    </li>
                  </ul>
                  <p>
                    Your continued use of ProvenLeap after the effective date of
                    an updated policy constitutes acceptance of the changes. If
                    you do not agree with the updated policy, you may terminate
                    your account.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Contact footer */}
              <footer className="border-t border-border pt-6 mt-8">
                <p className="text-base leading-relaxed max-w-prose">
                  If you have any questions about this Privacy Policy or our
                  data practices, please contact us at{" "}
                  <a
                    href="mailto:privacy@provenleap.com"
                    className="underline hover:text-foreground"
                  >
                    privacy@provenleap.com
                  </a>
                  .
                </p>
              </footer>
            </CardContent>
          </Card>
        </article>
      </div>
    </div>
  );
}
